import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "~/api";
import { useTranslation } from "react-i18next";
import { getCurrentCustomerId, customImagesAtom, feedbackModeAtom } from "~/AtomWithDynamoDB";
import { useIsSettingPage } from "~/utils/envUtils";
import { useAtom } from "jotai";
import { getCustomImagesWithSignedUrls } from "~/utils/customImageUtils";
import { CustomImageType } from "~/Commons";
import type { ConversationMessage } from "shared/Schemas";

// 日次データの型定義
export interface DailyData {
  date: string;
  count: number;
}

// 会話データの型定義
export interface ConversationData {
  id: string;
  name: string;
  inputs: Record<string, unknown>;
  introduction: string;
  created_at: number;
  created_at_string?: string;
  feedback?: { rating?: string; content?: string };
  messages?: ConversationMessage[];
}

// フィードバックデータの型定義
export interface FeedbackData {
  rating: string;
  count: number;
}

// 5段階モード用: 星評価データの型定義
export interface FeedbackDetailData {
  star: number;
  count: number;
}

/**
 * チャット履歴グラフのカスタムフック
 *
 * @returns チャット履歴グラフのカスタムフック
 */
export const useChatHistoryGraph = () => {
  const { t } = useTranslation();

  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [conversationsData, setConversationsData] = useState<ConversationData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feedbackData, setFeedbackData] = useState<FeedbackData[]>([]);
  const [feedbackDetailData, setFeedbackDetailData] = useState<FeedbackDetailData[]>([]);

  // フィードバックモード0: シンプルモード（GOOD/BAD）、1: 5段階モード（星1~5）
  const [feedbackMode, setFeedbackMode] = useAtom(feedbackModeAtom);

  // 日付範囲の状態管理
  const [startDate, setStartDate] = useState<Date>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date;
  });
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [filteredData, setFilteredData] = useState<ConversationData[]>([]);

  // カスタム画像関連
  const [customImages] = useAtom(customImagesAtom);
  const [customImagesLoaded, setCustomImagesLoaded] = useState<CustomImageType[]>([]);

  useEffect(() => {
    // カスタム画像の読み込み
    const fetchCustomImages = async () => {
      const systemRequirement = process.env.REACT_APP_SYSTEM_REQUIREMENT;
      const images = await getCustomImagesWithSignedUrls(customImages, systemRequirement);
      setCustomImagesLoaded(images);
    };
    fetchCustomImages();
  }, [customImages]);

  // シンプルモード用: ラベル変換
  const convertLabel = useCallback((value: string) => (value === "like" ? "GOOD" : "BAD"), []);

  // 5段階モード用: 星番号をラベルに変換
  const convertStarLabel = useCallback((star: number) => `★${star}`, []);

  // Web管理画面かどうか
  const isSettingPage = useIsSettingPage();

  /**
   * 日付フォーマット関数
   *
   * @param {Date} d - フォーマットする日付
   * @returns {string} フォーマットされた日付文字列（YYYY.MM.DD形式）
   */
  const formatDate = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}.${m}.${day}`;
  }, []);

  /**
   * 日付フォーマット関数（時:分付き）
   *
   * @param {Date} d - フォーマットする日付
   * @returns {string} フォーマットされた日付文字列（YYYY.MM.DD HH:MM形式）
   */
  const formatDateTimeYColon = useCallback((d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${y}.${m}.${day} ${hh}:${mm}`;
  }, []);

  /**
   * フィードバックの合計数を計算
   *
   * @returns {number} フィードバックの合計数
   */
  const totalFeedbackCount = useMemo(
    () => feedbackData.reduce((sum, d) => sum + (d.count || 0), 0),
    [feedbackData]
  );

  /**
   * 5段階モード用: 星評価の合計数を計算
   *
   * @returns {number} 星評価の合計数
   */
  const totalFeedbackDetailCount = useMemo(
    () => feedbackDetailData.reduce((sum, d) => sum + (d.count || 0), 0),
    [feedbackDetailData]
  );

  /**
   * フィードバック別評価数と割合を計算
   *
   * @returns {Array} フィードバック別評価数と割合の配列
   */
  const legendItems = useMemo(
    () =>
      feedbackData.map((d) => {
        const percent =
          totalFeedbackCount > 0 ? Math.round((d.count / totalFeedbackCount) * 100) : 0;
        return {
          key: d.rating,
          label: convertLabel(d.rating),
          count: d.count,
          percent,
        };
      }),
    [feedbackData, totalFeedbackCount, convertLabel]
  );

  /**
   * 5段階モード用: 星評価別評価数と割合を計算
   *
   * @returns {Array} 星評価別評価数と割合の配列
   */
  const legendDetailItems = useMemo(
    () =>
      feedbackDetailData.map((d) => {
        const percent =
          totalFeedbackDetailCount > 0 ? Math.round((d.count / totalFeedbackDetailCount) * 100) : 0;
        return {
          key: d.star,
          label: convertStarLabel(d.star),
          count: d.count,
          percent,
        };
      }),
    [feedbackDetailData, totalFeedbackDetailCount, convertStarLabel]
  );

  /**
   * データをAPIから取得
   *
   * @returns {Promise<void>} データ取得の非同期処理
   */
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // customerId を取得
      const customerId = await getCurrentCustomerId();

      const allDataResponse = await api.conversations.getAll.query({ customerId });
      if (!allDataResponse.success) {
        throw new Error(
          "error" in allDataResponse ? allDataResponse.error : t("failedToRetrieveConversationData")
        );
      }

      // 集計（daily/feedback/feedbackDetail）はクライアントサイドでapplyDateFilterで計算
      setConversationsData(allDataResponse.data as ConversationData[]);
    } catch (err) {
      console.error("データ取得エラー:", err);
      setError(err instanceof Error ? err.message : t("failedToRetrieveData"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  /**
   * 日付フィルタを適用
   *
   * @returns {void} 日付フィルタ適用後の処理
   */
  const applyDateFilter = useCallback(() => {
    if (conversationsData.length === 0) return;

    const filtered = conversationsData.filter((conv) => {
      const convDate = new Date(conv.created_at * 1000); // UNIXタイムスタンプをDateに変換
      const start = new Date(startDate);
      const end = new Date(endDate);

      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      return convDate >= start && convDate <= end;
    });

    setFilteredData(filtered);

    // 日別統計の計算
    const dailyStats: Record<string, number> = {};
    filtered.forEach((conv) => {
      const dateStr =
        conv.created_at_string || new Date(conv.created_at * 1000).toISOString().split("T")[0];
      const date = dateStr.split(" ")[0];
      dailyStats[date] = (dailyStats[date] || 0) + 1;
    });

    const dailyArray: DailyData[] = [];
    const currentDate = new Date(startDate);
    const endDateObj = new Date(endDate);

    while (currentDate <= endDateObj) {
      const dateStr = currentDate.toISOString().split("T")[0];
      dailyArray.push({
        date: dateStr,
        count: dailyStats[dateStr] || 0,
      });
      currentDate.setDate(currentDate.getDate() + 1);
    }

    setDailyData(dailyArray);

    // フィードバック集計をフィルタ済みデータから再計算
    const simpleFeedback: Record<string, number> = {};
    const starFeedback: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

    // eslint-disable-next-line complexity
    filtered.forEach((conv) => {
      const msgs = conv.messages;
      if (msgs && msgs.length > 0) {
        // メッセージレベルでフィードバックを集計
        msgs.forEach((msg) => {
          if (!msg.feedback?.rating) return;
          if (msg.feedback.content && /^[1-5]$/.test(msg.feedback.content)) {
            const star = Number(msg.feedback.content);
            starFeedback[star] = (starFeedback[star] || 0) + 1;
            const simpleRating = star <= 2 ? "dislike" : "like";
            simpleFeedback[simpleRating] = (simpleFeedback[simpleRating] || 0) + 1;
          } else {
            const rating = msg.feedback.rating;
            simpleFeedback[rating] = (simpleFeedback[rating] || 0) + 1;
          }
        });
      } else {
        // メッセージデータなし: 会話レベルのフィードバックにフォールバック
        if (!conv.feedback?.rating) return;
        if (conv.feedback.content && /^[1-5]$/.test(conv.feedback.content)) {
          const star = Number(conv.feedback.content);
          starFeedback[star] = (starFeedback[star] || 0) + 1;
          const simpleRating = star <= 2 ? "dislike" : "like";
          simpleFeedback[simpleRating] = (simpleFeedback[simpleRating] || 0) + 1;
        } else {
          const rating = conv.feedback.rating;
          simpleFeedback[rating] = (simpleFeedback[rating] || 0) + 1;
        }
      }
    });

    setFeedbackData(Object.entries(simpleFeedback).map(([rating, count]) => ({ rating, count })));
    // 星1〜5をすべて含め、円グラフで★5を右上から時計回りに並べるため降順ソート
    setFeedbackDetailData(
      [5, 4, 3, 2, 1].map((star) => ({ star, count: starFeedback[star] || 0 }))
    );
  }, [conversationsData, startDate, endDate]);

  // ウォームアップ完了時にデータを取得
  useEffect(() => {
    // ウォームアップ完了フラグをチェック
    const warmupKey = "difyWarmupCompleted";
    const shouldFetch = sessionStorage.getItem(warmupKey) === "true";
    let interval: NodeJS.Timeout | null = null;
    let isFetching = false;

    // ウォームアップ処理が完了、またはWeb管理画面の場合
    if (shouldFetch || isSettingPage) {
      fetchData();
    } else {
      // ウォームアップ未完了の場合は完了まで監視
      interval = setInterval(async () => {
        if (sessionStorage.getItem(warmupKey) === "true" && !isFetching) {
          isFetching = true;
          try {
            await fetchData();
          } finally {
            isFetching = false;
            if (interval) {
              clearInterval(interval);
              interval = null;
            }
          }
        }
      }, 3000); // 3秒ごとにポーリング
    }

    return () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };
  }, [fetchData, isSettingPage]);

  // 日付フィルタを適用
  useEffect(() => {
    applyDateFilter();
  }, [applyDateFilter]);

  /**
   * 日付範囲をリセット
   *
   * @returns {void} 日付範囲リセット後の処理
   */
  const resetDateRange = useCallback(() => {
    const newEndDate = new Date();
    const newStartDate = new Date();
    newStartDate.setDate(newStartDate.getDate() - 30);
    setStartDate(newStartDate);
    setEndDate(newEndDate);
  }, []);

  /**
   * CSVダウンロード
   *
   * @returns {void} CSVダウンロード後の処理
   */
  const downloadCSV = useCallback(() => {
    const dataToDownload = filteredData.length > 0 ? filteredData : conversationsData;

    if (dataToDownload.length === 0) {
      alert(`${t("thereIsNoDataToDownload")}`);
      return;
    }

    /**
     * HTMLタグを処理する関数（<br />は改行、<img>はURL保持、他は除去）
     *
     * @param html HTMLを含む文字列
     * @returns プレーンテキスト（imgタグのURLは保持）
     */
    const stripHtmlTags = (html: string): string => {
      if (!html) return "";
      // <img>タグのsrc属性を抽出して [画像: URL] 形式に変換（より堅牢な正規表現）
      let text = html.replace(/<img[^>]+src=["']([^"']*?)["'][^>]*>/gi, "[画像: $1]");
      // <br />と<br>を改行文字に変換
      text = text.replace(/<br\s*\/?>/gi, "\n");
      // その他のHTMLタグを除去
      text = text.replace(/<[^>]+>/g, "");
      // 3つ以上連続する改行を2つに正規化
      text = text.replace(/\n{3,}/g, "\n\n");
      // 前後の空白を削除
      text = text.trim();
      return text;
    };

    /**
     * JSONパース済みのqueryからメッセージを抽出
     *
     * @param query クエリ文字列（JSON形式の場合がある）
     * @returns 実際のメッセージ文字列
     */
    const extractQueryMessage = (query: string): string => {
      if (!query) return "";
      try {
        // まずそのままJSONとしてパースを試行
        const parsed = JSON.parse(query);
        return parsed.message || query;
      } catch {
        try {
          // 単一引用符を二重引用符に変換してJSONパース試行
          // ユニークなプレースホルダーを生成（タイムスタンプとランダム値の組み合わせ）
          const placeholder = `__ESC_${Date.now()}_${Math.random().toString(36).substring(2)}_QUOTE__`;
          // エスケープされた単一引用符をプレースホルダーに置換
          let normalized = query.replace(/\\'/g, placeholder);
          // 通常の単一引用符を二重引用符に置換
          normalized = normalized.replace(/'/g, '"');
          // プレースホルダーをエスケープされた二重引用符に戻す
          normalized = normalized.replace(new RegExp(placeholder, "g"), '\\"');
          const parsed = JSON.parse(normalized);
          return parsed.message || query;
        } catch {
          // JSON形式でない場合はそのまま返す
          return query;
        }
      }
    };

    /**
     * CSV用に文字列をエスケープ
     *
     * @param str エスケープする文字列
     * @returns エスケープされた文字列
     */
    const escapeCsvField = (str: string): string => {
      if (!str) return "";
      // ダブルクォートを2つに
      const escaped = str.replace(/"/g, '""');
      // カンマ、改行、ダブルクォートを含む場合はダブルクォートで囲む
      if (escaped.includes(",") || escaped.includes("\n") || escaped.includes('"')) {
        return `"${escaped}"`;
      }
      return escaped;
    };

    // CSVヘッダー(日本語固定)
    const headers = [
      "ID",
      "名前",
      "作成日時",
      "フィードバック",
      "メッセージ番号",
      "ユーザー質問",
      "AI回答",
    ];

    // CSVデータを作成（各メッセージを個別の行として展開）
    const csvRows: string[][] = [];

    dataToDownload.forEach((item) => {
      const baseConvInfo = [
        item.id,
        escapeCsvField(item.name),
        item.created_at_string ||
          new Date(item.created_at * 1000).toISOString().slice(0, 19).replace("T", " "),
      ];

      // メッセージがある場合は各メッセージを個別の行として追加
      if (item.messages && item.messages.length > 0) {
        item.messages.forEach((message, index) => {
          // メッセージ単位でフィードバック値を判定
          const msgFb = message.feedback;
          const feedbackValue =
            msgFb?.content && /^[1-5]$/.test(msgFb.content)
              ? `★${msgFb.content}`
              : msgFb?.rating
                ? msgFb.rating
                : `${t("unrated")}`;

          const userQuestion = extractQueryMessage(message.query);
          const aiAnswer = stripHtmlTags(message.answer);

          csvRows.push([
            ...baseConvInfo,
            feedbackValue,
            String(index + 1), // メッセージ番号（1から開始）
            escapeCsvField(userQuestion),
            escapeCsvField(aiAnswer),
          ]);
        });
      } else {
        // メッセージがない場合は会話レベルのフィードバックで1行追加
        const convFb = item.feedback;
        const feedbackValue =
          convFb?.content && /^[1-5]$/.test(convFb.content)
            ? `★${convFb.content}`
            : convFb?.rating
              ? convFb.rating
              : `${t("unrated")}`;
        csvRows.push([...baseConvInfo, feedbackValue, "", "", `${t("noConversation")}`]);
      }
    });

    // CSVコンテンツを作成
    const csvContent = [headers.join(","), ...csvRows.map((row) => row.join(","))].join("\n");

    // BOMを追加してUTF-8で正しく表示させる
    const bom = "\uFEFF";
    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });

    // ダウンロード用のリンクを作成
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);

    // ファイル名に現在の日時を含める
    const now = new Date();
    const timestamp = now.toISOString().slice(0, 19).replace(/[T:]/g, "_");
    link.setAttribute("download", `conversations_${timestamp}.csv`);

    // ダウンロードを実行
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }, [filteredData, conversationsData, t]);

  return {
    // state
    dailyData,
    conversationsData,
    loading,
    error,
    feedbackData,
    feedbackDetailData,
    feedbackMode,
    setFeedbackMode,
    startDate,
    endDate,
    filteredData,
    customImagesLoaded,

    // computed
    totalFeedbackCount,
    totalFeedbackDetailCount,
    legendItems,
    legendDetailItems,

    // methods
    setStartDate,
    setEndDate,
    fetchData,
    resetDateRange,
    downloadCSV,
    convertLabel,
    convertStarLabel,
    formatDate,
    formatDateTimeYColon,
  };
};
